// api/axiosInstance.js
import { SpanKind, SpanStatusCode, trace } from "@opentelemetry/api";
import axios, { AxiosError, InternalAxiosRequestConfig } from "axios";

import { getEnv } from "../lib/get_env";

const tracer = trace.getTracer("customer-portal-ui");

let getTokenFn: (() => string | null) | null = null;
let refreshTokenFn: (() => Promise<string | null>) | null = null;

export const setTokenGetter = (fn: () => string | null) => {
  getTokenFn = fn;
};

export const setTokenRefresher = (fn: () => Promise<string | null>) => {
  refreshTokenFn = fn;
};

const instance = axios.create({
  baseURL: `${getEnv("VITE_BACKEND_SERVICE_URL")}`,
  headers: {
    "Content-Type": "application/json",
  },
});

// Add a request interceptor
instance.interceptors.request.use(
  (config) => {
    const span = tracer.startSpan("http.request.prepare", {
      kind: SpanKind.CLIENT,
      attributes: {
        "http.method": config.method?.toUpperCase() || "GET",
        "http.url": config.url || "",
        "http.base_url": config.baseURL || "",
      },
    });

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (config as any)._otelSpan = span;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (config as any)._otelSpanStartTime = Date.now();

    if (getTokenFn) {
      const token = getTokenFn();
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
        span.setAttribute("auth.token_present", true);
      } else {
        span.setAttribute("auth.token_present", false);
      }
    }

    span.end();
    return config;
  },
  (error) => {
    const span = tracer.startSpan("http.request.prepare_failed");
    span.setAttribute("error.message", error instanceof Error ? error.message : String(error));
    span.setStatus({ code: SpanStatusCode.ERROR });
    span.end();
    return Promise.reject(error);
  },
);

// Add a response interceptor to handle 401 errors and token refresh
let isRefreshing = false;
let failedQueue: Array<{
  resolve: (token: string | null) => void;
  reject: (error: Error) => void;
}> = [];

const processQueue = (error: Error | null, token: string | null = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

instance.interceptors.response.use(
  (response) => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const config = response.config as any;
    if (config._otelSpan && config._otelSpanStartTime) {
      const duration = Date.now() - config._otelSpanStartTime;

      const span = tracer.startSpan("http.request.complete", {
        attributes: {
          "http.method": config.method?.toUpperCase() || "GET",
          "http.url": config.url || "",
          "http.status_code": response.status,
          "http.duration_ms": duration,
        },
      });
      span.end();
    }
    return response;
  },
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & {
      _retry?: boolean;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      _otelSpan?: any;
      _otelSpanStartTime?: number;
    };

    if (error.response?.status === 401 && !originalRequest._retry) {
      const span = tracer.startSpan("auth.unauthorized_detected", {
        attributes: {
          "http.method": originalRequest.method?.toUpperCase() || "GET",
          "http.url": originalRequest.url || "",
          "http.status_code": 401,
        },
      });
      span.end();

      if (isRefreshing) {
        const queueSpan = tracer.startSpan("auth.token_refresh.queued");
        try {
          const result = await new Promise((resolve, reject) => {
            failedQueue.push({ resolve, reject });
          });
          queueSpan.setAttribute("queue.resolution", "success");
          queueSpan.end();

          if (result && originalRequest.headers) {
            originalRequest.headers.Authorization = `Bearer ${result}`;
          }
          return instance(originalRequest);
        } catch (err) {
          queueSpan.setAttribute("queue.resolution", "failed");
          queueSpan.setStatus({ code: SpanStatusCode.ERROR });
          queueSpan.end();
          return Promise.reject(err);
        }
      }

      originalRequest._retry = true;
      isRefreshing = true;

      if (refreshTokenFn) {
        const refreshSpan = tracer.startSpan("auth.token_refresh.attempt");
        try {
          const newToken = await refreshTokenFn();
          if (newToken && originalRequest.headers) {
            originalRequest.headers.Authorization = `Bearer ${newToken}`;
            refreshSpan.setAttribute("refresh.success", true);
            refreshSpan.addEvent("token_refreshed");
            processQueue(null, newToken);
            isRefreshing = false;
            refreshSpan.end();
            return instance(originalRequest);
          } else {
            refreshSpan.setAttribute("refresh.success", false);
            refreshSpan.setAttribute("refresh.reason", "no_token_returned");
            const noTokenError = new Error("Token refresh returned no token");
            processQueue(noTokenError, null);
            isRefreshing = false;
            refreshSpan.end();
            return Promise.reject(noTokenError);
          }
        } catch (refreshError) {
          refreshSpan.setAttribute("refresh.success", false);
          refreshSpan.setAttribute(
            "error.message",
            refreshError instanceof Error ? refreshError.message : String(refreshError),
          );
          refreshSpan.setStatus({ code: SpanStatusCode.ERROR });
          refreshSpan.end();
          processQueue(refreshError instanceof Error ? refreshError : null, null);
          isRefreshing = false;
          return Promise.reject(refreshError);
        }
      }

      isRefreshing = false;
    }

    if (originalRequest._otelSpanStartTime) {
      const duration = Date.now() - originalRequest._otelSpanStartTime;
      const errorSpan = tracer.startSpan("http.request.failed", {
        attributes: {
          "http.method": originalRequest.method?.toUpperCase() || "GET",
          "http.url": originalRequest.url || "",
          "http.status_code": error.response?.status || 0,
          "http.duration_ms": duration,
          "error.message": error.message,
        },
      });
      errorSpan.setStatus({ code: SpanStatusCode.ERROR, message: error.message });
      errorSpan.end();
    }

    return Promise.reject(error);
  },
);

export { instance as useApiInstance };
